'use client';

import {
  FactSlogan,
  freddyTheme,
  GridItem,
  IconButton,
  Input,
  InputAdornment,
  Link as FreddyLink
} from '@coolblue-development/freddy';
import buttonInteractionTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentInteraction/buttonInteractionTrigger';
import searchInteractionTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentInteraction/searchInteractionTrigger';
import { ArrowRight, Magnifier } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import { useClientUrl } from '@utils/url-generators/useClientUrl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';

export const HeroCard = () => {
  const { t } = useTranslation('homePage');
  const { trackEvent } = useAnalytics();

  const router = useRouter();
  const filterPageUrl = useClientUrl('filterPage')!;

  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const executeSearch = (query: string) => {
    if (!isSearching) {
      setIsSearching(true);
      const queryString = query.length
        ? `?${new URLSearchParams([['query', query]]).toString()}`
        : '';
      router.push(`${filterPageUrl}${queryString}`);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      trackEvent(searchInteractionTrigger({ search_term: searchValue, method: 'enter' }));
      executeSearch(searchValue);
    }
  };

  return (
    <GridItem
      backgroundColor={freddyTheme.color.background.base}
      padding={{
        xs: freddyTheme.spacing[400],
        md: `${freddyTheme.spacing[400]} ${freddyTheme.spacing[500]}`,
        lg: freddyTheme.spacing[700],
        xl: freddyTheme.spacing[1000]
      }}
      sx={{
        height: '140px',
        [freddyTheme.breakpoint.minWidth('lg')]: {
          gridColumn: '2 / 12',
          height: 'auto',
          zIndex: 1,
          boxShadow: freddyTheme.shadow.strong,
          borderRadius: freddyTheme.borderRadius[200]
        }
      }}
    >
      <FactSlogan
        fact={t('heroCard.fact')}
        slogan={t('heroCard.slogan')}
        size={{
          xs: 'small',
          md: 'medium',
          xl: 'xlarge'
        }}
        sx={{
          paddingBottom: freddyTheme.spacing[400],
          [freddyTheme.breakpoint.minWidth('lg')]: { paddingBottom: freddyTheme.spacing[600] }
        }}
      />
      <Input
        type='search'
        value={searchValue}
        onChange={e => setSearchValue(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        disabled={isSearching}
        endAdornment={
          <InputAdornment>
            <IconButton
              onClick={() => {
                trackEvent(searchInteractionTrigger({ search_term: searchValue, method: 'click' }));
                executeSearch(searchValue);
              }}
              iconColor='inverse'
              aria-label='Search'
              disabled={isSearching}
              sx={{
                backgroundColor: freddyTheme.color.background.primary,
                borderRadius: `0 ${freddyTheme.borderRadius[100]} ${freddyTheme.borderRadius[100]} 0`
              }}
            >
              <Magnifier />
            </IconButton>
          </InputAdornment>
        }
        placeholder={t('heroCard.searchPlaceholder')}
      />
      <FreddyLink
        component={Link}
        startIcon={<ArrowRight />}
        marginTop={freddyTheme.spacing[400]}
        href={filterPageUrl}
        rel='nofollow'
        onClick={() => {
          setIsSearching(true);
          trackEvent(
            buttonInteractionTrigger({
              content: t('heroCard.allVacanciesLink'),
              sub_feature: 'all_vacancies'
            })
          );
        }}
        aria-label={t('heroCard.allVacanciesLink')}
      >
        {t('heroCard.allVacanciesLink')}
      </FreddyLink>
    </GridItem>
  );
};
